import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedSnackbar,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import {
	CLIENT_SETUP_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { formatDate, scrollPanelFieldsToTop, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BasicSetupFormToolbar } from "../BasicSetupFormToolbar";
import { BillingProfileService } from "../../services/BillingProfileService";
import { BillingProfileSetupLeftPanel } from "./Panels/BillingProfileSetupLeftPanel";
import { BillingProfileSetupRightPanel } from "./Panels/BillingProfileSetupRightPanel";
import { BillingProfileSetupSchema } from "./validator";
import { BillingProfileSlabsFieldsPanel } from "./Panels/BillingProfileSlabsPanel";
import { DDLDataTable } from "../DDLDataTable";
import { IAppState } from "../../store/reducers";
import { setSelectedActivityLogActions } from "../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IBillingProfile { }
const SINGLE_RECORD_DATA_QUERY_KEY = "billing-Profile-single-data-record";

const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return BillingProfileService.getById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetBillingProfileDDL = () =>
	useQuery({
		queryKey: ["billing-profile-ddl"],
		queryFn: (data) => {
			return BillingProfileService.getBillingProfileDDL();
		},
		refetchOnWindowFocus: false,
	});
export const BillingProfileSetup: React.FC<IBillingProfile> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isPendingActivityLogData = useSelector(
		(state: IAppState) => state.isLoadAxiosCall.isLoading
	);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [billingProfileId, setBillingProfileId] = useState<any>(0);
	const [isPendingData, setIsPendingData] = useState(false);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const { isPending: isSubmitting, mutate: submitFormFields } = useMutation({
		mutationFn: BillingProfileService.create,

		onSuccess: (res: IBillingProfile | IServerError | IResponse | any) => {

			if (res && res.message === "Success") {
				setEditRecordId(res.id);
				queryClient.invalidateQueries({
					queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
				});
				queryClient.invalidateQueries({
					queryKey: ["billing-profile-ddl"],
				});
				setSnackbarData({
					open: true,
					message: res.message,
					severity: "success",
				});
				scrollPanelFieldsToTop();
				let path = `/billing-profile/${res.data.id}`;
				navigate(path);
			} else if (res && res.message === "Success") {
				queryClient.invalidateQueries({
					queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
				});
				queryClient.invalidateQueries({
					queryKey: ["billing-profile-ddl"],
				});
				setSnackbarData({
					open: true,
					message: res.message,
					severity: "success",
				});
				scrollPanelFieldsToTop();
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const { isPending: isPendinguseBillingProfileDDL, data: BillingProfileDDL } =
		useGetBillingProfileDDL();

	const DEFAULT_VALUES = {
		profileLabel: undefined,
		dateEffective: null,
		endDate: null,
		profileType: 100,

		weeklyCheque: null,
		weeklyDirectDeposit: null,
		minInvoice: null,
		minWeeklyInvoice: null,
		setupFee: null,
		statHoliday: null,

		isGlobal: false,
		t4: null,
		t4Summary: null,
		payrollRemmitance: null,
		ehtRemmitance: null,
		statHolidaywsibRemmitance: null,
		directDepositFile: null,
		netPaySplitAccount: null,
		bussPaymentCheque: null,
		bussPaymentDd: null,
		quebecRemmitance: null,
		employeeAdvance: null,
		invoiceGeneration: 100,
		// monthly fee
		chargesType: "flat slab charges",
		monthlySlabs: 1,
		monthlySlabsCharges: "",
		ddSlabs: 1,
		ddSlabsCharges: "",
		chqSlabs: 1,
		chqSlabsCharges: "",
		ddChqSlabs: 1,
		ddChqSlabsCharges: "",
		// billing profile steup
		discountNonTaxablePercentage: null,
		discountNonTaxableAmount: null,

		discountTaxablePercentage: null,
		discountTaxableAmount: null,
		effectiveDate: null,
		nfsPenalty: null,
		td1: null,
		yearEndCompliance: null,
		inactivePeriod: null,
		wireTransferCharges: null,
		baseRun: null,
		foldSeal: null,
		archiving: null,
		delivery: null,
		t4Xml: null,
		taxForms: null,
		electronicCraRemittance: null,
		timeAttendance: null,
		bussPaySplitAccount: null,
		roe: null,
		roeWebFileByClientPerFile: null,
		roeWebFileBySuPerEmp: null,
		netSuitePerMonth: null,
	};
	const formMethods = useForm<any>({
		resolver: yupResolver(BillingProfileSetupSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const onSubmit = async (values: any) => {
		console.log('values', values)

		let monthlySlabsItems = [];
		if (values.monthlySlabs1 && values.monthlySlabs1 !== "" && values.monthlySlabsCharges1 !== null) {
			monthlySlabsItems.push({
				slab: values.monthlySlabs1,
				charges: values.monthlySlabsCharges1,
			});
		}

		if (values.monthlySlabs2 && values.monthlySlabs2 !== "" && values.monthlySlabsCharges2 !== null) {
			monthlySlabsItems.push({
				slab: values.monthlySlabs2,
				charges: values.monthlySlabsCharges2,
			});
		}

		if (values.monthlySlabs3 && values.monthlySlabs3 !== "" && values.monthlySlabsCharges3 !== null) {
			monthlySlabsItems.push({
				slab: values.monthlySlabs3,
				charges: values.monthlySlabsCharges3,
			});
		}

		if (values.monthlySlabs4 && values.monthlySlabs4 !== "" && values.monthlySlabsCharges4 !== null) {
			monthlySlabsItems.push({
				slab: values.monthlySlabs4,
				charges: values.monthlySlabsCharges4,
			});
		}

		if (values.monthlySlabs5 && values.monthlySlabs5 !== "" && values.monthlySlabsCharges5 !== null) {
			monthlySlabsItems.push({
				slab: values.monthlySlabs5,
				charges: values.monthlySlabsCharges5,
			});
		}

		let ddSlabsItems = [];
		if (values.ddSlabs1 && values.ddSlabs1 !== "" && values.ddSlabsCharges1 !== null) {
			ddSlabsItems.push({
				slab: values.ddSlabs1,
				charges: values.ddSlabsCharges1,
			});
		}

		if (values.ddSlabs2 && values.ddSlabs2 !== "" && values.ddSlabsCharges2 !== null) {
			ddSlabsItems.push({
				slab: values.ddSlabs2,
				charges: values.ddSlabsCharges2,
			});
		}

		if (values.ddSlabs3 && values.ddSlabs3 !== "" && values.ddSlabsCharges3 !== null) {
			ddSlabsItems.push({
				slab: values.ddSlabs3,
				charges: values.ddSlabsCharges3,
			});
		}

		if (values.ddSlabs4 && values.ddSlabs4 !== "" && values.ddSlabsCharges4 !== null) {
			ddSlabsItems.push({
				slab: values.ddSlabs4,
				charges: values.ddSlabsCharges4,
			});
		}

		if (values.ddSlabs5 && values.ddSlabs5 !== "" && values.ddSlabsCharges5 !== null) {
			ddSlabsItems.push({
				slab: values.ddSlabs5,
				charges: values.ddSlabsCharges5,
			});
		}

		let chqSlabsItems = [];
		if (values.chqSlabs1 && values.chqSlabs1 !== "" && values.chqSlabsCharges1 !== null) {
			chqSlabsItems.push({
				slab: values.chqSlabs1,
				charges: values.chqSlabsCharges1,
			});
		}

		if (values.chqSlabs2 && values.chqSlabs2 !== "" && values.chqSlabsCharges2 !== null) {
			chqSlabsItems.push({
				slab: values.chqSlabs2,
				charges: values.chqSlabsCharges2,
			});
		}

		if (values.chqSlabs3 && values.chqSlabs3 !== "" && values.chqSlabsCharges3 !== null) {
			chqSlabsItems.push({
				slab: values.chqSlabs3,
				charges: values.chqSlabsCharges3,
			});
		}

		if (values.chqSlabs4 && values.chqSlabs4 !== "" && values.chqSlabsCharges4 !== null) {
			chqSlabsItems.push({
				slab: values.chqSlabs4,
				charges: values.chqSlabsCharges4,
			});
		}

		if (values.chqSlabs5 && values.chqSlabs5 !== "" && values.chqSlabsCharges5 !== null) {
			chqSlabsItems.push({
				slab: values.chqSlabs5,
				charges: values.chqSlabsCharges5,
			});
		}

		let ddChqSlabsItems = [];
		if (values.ddChqSlabs1 && values.ddChqSlabs1 !== "" && values.ddChqSlabsCharges1 !== null) {
			ddChqSlabsItems.push({
				slab: values.ddChqSlabs1,
				charges: values.ddChqSlabsCharges1,
			});
		}

		if (values.ddChqSlabs2 && values.ddChqSlabs2 !== "" && values.ddChqSlabsCharges2 !== null) {
			ddChqSlabsItems.push({
				slab: values.ddChqSlabs2,
				charges: values.ddChqSlabsCharges2,
			});
		}

		if (values.ddChqSlabs3 && values.ddChqSlabs3 !== "" && values.ddChqSlabsCharges3 !== null) {
			ddChqSlabsItems.push({
				slab: values.ddChqSlabs3,
				charges: values.ddChqSlabsCharges3,
			});
		}

		if (values.ddChqSlabs4 && values.ddChqSlabs4 !== "" && values.ddChqSlabsCharges4 !== null) {
			ddChqSlabsItems.push({
				slab: values.ddChqSlabs4,
				charges: values.ddChqSlabsCharges4,
			});
		}

		if (values.ddChqSlabs5 && values.ddChqSlabs5 !== "" && values.ddChqSlabsCharges5 !== null) {
			ddChqSlabsItems.push({
				slab: values.ddChqSlabs5,
				charges: values.ddChqSlabsCharges5,
			});
		}

		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			profileLabel: values.profileLabel,
			dateEffective: formatDate(values.dateEffective),
			endDate:
				values.endDate !== "" && values.endDate !== null
					? formatDate(values.endDate)
					: values.endDate,
			isGlobal: values.isGlobal,
			profileType: values.profileType,
			directDeposit: values.directDeposit,
			chequeEmployee: values.chequeEmployee,
			weeklyCheque: values.weeklyCheque,
			weeklyDirectDeposit: values.weeklyDirectDeposit,
			minInvoice: values.minInvoice,
			minWeeklyInvoice: values.minWeeklyInvoice,
			setupFee: values.setupFee,
			statHoliday: values.statHoliday,
			t4: values.t4,
			t4Summary: values.t4Summary,
			payrollRemmitance: values.payrollRemmitance,
			ehtRemmitance: values.ehtRemmitance,
			statHolidaywsibRemmitance: values.statHolidaywsibRemmitance,
			directDepositFile: values.directDepositFile,
			netPaySplitAccount: values.netPaySplitAccount,
			bussPaymentCheque: values.bussPaymentCheque,
			bussPaymentDd: values.bussPaymentDd,
			quebecRemmitance: values.quebecRemmitance,
			billingProfileAdvance: values.billingProfileAdvance,
			invoiceGeneration: values.invoiceGeneration,
			discountSelected: values.discountSelected,
			discountNonTaxablePercentage: values.discountNonTaxablePercentage,
			discountNonTaxableAmount: values.discountNonTaxableAmount,
			discountTaxablePercentage: values.discountTaxablePercentage,
			discountTaxableAmount: values.discountTaxableAmount,
			nfsPenalty: values.nfsPenalty,
			td1: values.td1,
			yearEndCompliance: values.yearEndCompliance,
			inactivePeriod: values.inactivePeriod,
			wireTransferCharges: values.wireTransferCharges,
			baseRun: values.baseRun,
			foldSeal: values.foldSeal,
			archiving: values.archiving,
			delivery: values.delivery,
			t4Xml: values.t4Xml,
			taxForms: values.taxForms,
			electronicCraRemittance: values.electronicCraRemittance,
			timeAttendance: values.timeAttendance,
			bussPaySplitAccount: values.bussPaySplitAccount,
			roe: values.roe,
			roeWebFileByClientPerFile: values.roeWebFileByClientPerFile,
			roeWebFileBySuPerEmp: values.roeWebFileBySuPerEmp,
			netSuitePerMonth: values.netSuitePerMonth,
			monthlyFee: {
				monthlySlabs: {
					slabCharges: monthlySlabsItems.length > 0 ? monthlySlabsItems : null,
				},
				ddSlabs: {
					slabCharges: ddSlabsItems.length > 0 ? ddSlabsItems : null,
				},
				chqSlabs: {
					slabCharges: chqSlabsItems.length > 0 ? chqSlabsItems : null,
				},
				ddChqSlabs: {
					slabCharges: ddChqSlabsItems.length > 0 ? ddChqSlabsItems : null,
				},
			},
		};

		submitFormFields(payload);
	};

	useEffect(() => {

		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				if (typeof fieldValue !== "object") {
					formMethods.setValue(fieldName, fieldValue ?? undefined);
					if (fieldName === "dateEffective" && fieldValue !== null) {
						formMethods.setValue(fieldName, new Date(fieldValue));
					}
					if (fieldName === "endDate" && fieldValue !== null) {
						formMethods.setValue(fieldName, new Date(fieldValue));
					}
				} else if (fieldValue !== null || undefined) {
					formMethods.setValue(fieldName, fieldValue ?? undefined);
					Object.keys(fieldValue).forEach((objName: any) => {
						const objValue = fieldValue[objName];
						if (typeof objValue !== "object") {
							formMethods.setValue(objName, objValue ?? undefined);
						} else {
							Object.keys(objValue).forEach((subObjName: any) => {
								const SubObjValue = objValue[subObjName];
								if (typeof SubObjValue !== "object") {
									let fullName = objName + subObjName;
									formMethods.setValue(
										fullName,
										Number(SubObjValue).toFixed(2) ?? undefined
									);
								} else if (
									typeof SubObjValue === "object" &&
									SubObjValue !== null
								) {

									SubObjValue &&
										SubObjValue.map((item: any, index: any) => {
											let slabIdName = objName + (index + 1);

											formMethods.setValue(slabIdName, item.slab ?? undefined);

											let slipSlabChargesIdName =
												objName + "Charges" + (index + 1);

											formMethods.setValue(
												slipSlabChargesIdName,
												item.charges.toFixed(2) ?? undefined
											);
										});
								}
							});
						}
					});
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteBillingProfileById } =
		useMutation({
			mutationFn: BillingProfileService.delBillingProfile,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the BillingProfile fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				scrollPanelFieldsToTop();
				queryClient.invalidateQueries({
					queryKey: ["billing-profile-ddl"],
				});
				let path = `/billing-profile`;
				navigate(path);
				setEditRecordId(undefined)
			},
			onError: (err) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteBillingProfileField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				billingProfileId: billingProfileId,
				navigateToLogout: navigateToLogout,
			};
			deleteBillingProfileById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setBillingProfileId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
		let path = `/billing-profile`;
		navigate(path);
	};

	const handleClick = (value: string) => {
		formMethods.reset();
		setEditRecordId(value);
	};

	const upadtedBilingProfileListItems = useMemo(() => {
		return (
			BillingProfileDDL &&
			sortDDLByLabel(BillingProfileDDL).map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/billing-profile/${item.value}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => handleClick(item.value)}
					>
						{item.label}
					</Link>
				),
			}))
		);
	}, [BillingProfileDDL]);

	const expandedLabels = ["Misc."];
	const expandedBillingProfileListOrBillingProfileItem = "Billing Profile";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Misc." &&
				item.label === expandedBillingProfileListOrBillingProfileItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedBillingProfileListOrBillingProfileItem && "/billing-profile",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: id ? id : "",
				entityId2: "",
				entityId3: "",
				screenName: CLIENT_SETUP_SCREEN_SLUG,
				// accountantName: clientName,
				activityType:
					"activity_type=create&activity_type=updated&activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingById ||
					isDeleting ||
					isPendingData ||
					isPendingActivityLogData ||
					isPendinguseBillingProfileDDL
				}
				testId="accountant Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteBillingProfileField}
				cancelActionHandler={onCloseHandler}
				testId="accountant-confirmation-dialog"
			/>
			<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<Grid
					size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
					sx={{ "& .MuiGrid-container": { marginTop: 0 } }}
				>
					<FormProvider {...formMethods}>
						<form onSubmit={formMethods.handleSubmit(onSubmit)}>
							<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Grid
									size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
									sx={{
										// border: "1px solid rgba(0, 0, 0, 0.12)",
										borderRadius: "3px",
										// marginTop: "16px",
										// background: "#ffffff",
									}}
								>
									<BasicSetupFormToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										// isSubmitting={isSubmitting}
										id={id}
										pageTitle={"Billing Profile"}
									/>
									<TwoColumnLayout
										leftColumnContent={
											<>
												<StandardPanel
													title="Billing Profile Setup"
													testId={`billing-profile-setup`}
													variant="outlined"
													// borderBottom={true}
													borderHide={true}
												>
													<BillingProfileSetupLeftPanel
														formMethods={formMethods}
														testId="billing-profile-setup"
													/>
												</StandardPanel>
											</>
										}
										rightColumnContent={
											<>
												<StandardPanel
													title="Billing Profile Setup"
													testId={`billing-profile-setup`}
													variant="outlined"
													// borderBottom={true}
													borderHide={true}
												>
													<BillingProfileSetupRightPanel
														formMethods={formMethods}
														testId="billing-profile-setup"
													/>
												</StandardPanel>
												{/* <StandardPanel
													title="Monthly Fee"
													testId={`monthly-fee`}
													variant="outlined"
													borderBottom={true}
													borderHide={true}
												>
													<BillingProfileSlabsFieldsPanel 
													testId={"billing-profile-slabs-setup"}/>
												</StandardPanel> */}
											</>
										}
										toolbar={
											// <BasicSetupFormToolbar
											// 	onResetClicked={onResetClicked}
											// 	onDeleteClicked={onDeleteClicked}
											// 	// isSubmitting={isSubmitting}
											// 	id={id}
											// 	pageTitle={"Billing Profile"}
											// />
											<></>
										}
										testId="form-layout"
										showBorder={true}
									/>
									<StandardPanel
										title="Monthly Fee"
										testId={`monthly-fee`}
										variant="outlined"
										borderBottom={true}
										// borderHide={false}
										topPadding={2}
									>
										<BillingProfileSlabsFieldsPanel
											testId={"billing-profile-slabs-setup"}
										/>
									</StandardPanel>
								</Grid>
								<Grid size={{ xs: 0, sm: 0, md: 1.3, lg: 1.3, xl: 1.3 }}></Grid>
								<Grid
									size={{ xs: 12, sm: 12, md: 2.7, lg: 2.7, xl: 2.7 }}
									sx={{ marginTop: "16px" }}
								>
									<DDLDataTable
										rows={upadtedBilingProfileListItems}
										showDeleteIcon={false}
										isLoading={false}
										totalRecords={0}
										initialPageSize={INITIAL_PAGE_SIZE}
										rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
										tableScrollHeight={"60vh"}
										selectedId={selectedDataRecord && id && selectedDataRecord.id}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</Grid>
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"accountant-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
